import React, { useState, Fragment, ChangeEvent } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FeedbackType } from '../../../../api/feedback';
import { Button } from '../../../onboarding/components';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

interface QuickAction {
  id: string;
  label: string;
}

const positiveQuickActions = (t: TFunction<'pleri/chat'>): QuickAction[] => [
  { id: 'great_explanation', label: t('clearHelpfulExplanation') },
  { id: 'solved_problem', label: t('solvedMyProblem') },
  { id: 'good_code_quality', label: t('highQualityCode') },
  { id: 'fast_response', label: t('fastTimelyResponse') },
  { id: 'good_sources', label: t('reliableWellSourced') },
  { id: 'good_security', label: t('securityBestPractices') },
  { id: 'innovative', label: t('innovativeCreativeSolution') },
];

const negativeQuickActions = (t: TFunction<'pleri/chat'>): QuickAction[] => [
  { id: 'incorrect_solution', label: t('incorrectOrMisleadingAnswer') },
  { id: 'poor_explanation', label: t('confusingOrLackingExplanation') },
  { id: 'poor_code_quality', label: t('lowQualityCode') },
  { id: 'slow_response', label: t('slowOrDelayedResponse') },
  { id: 'bad_sources', label: t('unreliableOrPoorSources') },
  { id: 'security_issues', label: t('potentialSecurityRisks') },
  { id: 'not_solved', label: t('didNotSolveMyProblem') },
];

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  feedbackType: FeedbackType;
  onSubmit: (message?: string) => Promise<void>;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  isOpen,
  onClose,
  feedbackType,
  onSubmit,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [message, setMessage] = useState('');
  const [selectedQuickAction, setSelectedQuickAction] = useState<string | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const quickActions =
    feedbackType === 'upvote'
      ? positiveQuickActions(t)
      : negativeQuickActions(t);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const fullMessage = selectedQuickAction
        ? `${quickActions.find((a) => a.id === selectedQuickAction)?.label}${message ? ': ' + message : ''}`
        : message;
      await onSubmit(fullMessage || undefined);
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[1212121212121212121123123]"
        onClose={onClose}
        id="search-app"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-[1212121212121212121123123] bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 z-[1212121212121212121123123] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all dark:bg-gray-900">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium text-gray-900 dark:text-gray-100"
                >
                  {t('tellUsMore')}
                </Dialog.Title>

                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {quickActions.map((action) => (
                      <button
                        key={action.id}
                        onClick={() =>
                          setSelectedQuickAction(
                            action.id === selectedQuickAction
                              ? null
                              : action.id,
                          )
                        }
                        className={`rounded-md border px-3 py-1.5 text-sm transition-all ${
                          action.id === selectedQuickAction
                            ? 'border-[#FF732E] bg-orange-50 text-[#FF732E] dark:bg-gray-800'
                            : 'border-gray-200 text-gray-600 hover:border-[#FF732E]/30 hover:bg-orange-50/50 dark:border-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <textarea
                    value={message}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      setMessage(e.target.value)
                    }
                    placeholder={t('additionalCommentsOptional')}
                    maxLength={500}
                    rows={3}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-[#FF732E] focus:ring-1 focus:ring-[#FF732E] focus:outline-none dark:border-gray-700 dark:placeholder:text-gray-500"
                    aria-label={t('additionalFeedback')}
                  />
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    {t('common:cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                  >
                    {t('submit')}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
